/**
 * Shared formatting utilities for financial cards to ensure consistent alignment
 */

export interface FinancialData {
  commissionData?: {
    commissionRate: number;
    commissionShare: number;
    shipments: Array<{
      shipmentNumber: string;
      commissionAmount: number;
      expenses: number;
      status: string;
    }>;
  };
  paymentData?: {
    payments: Array<{
      amount: number;
    }>;
    totalCommission: number;
  };
  contractData?: any; // For calculating shipment commissions
}

export class FinancialFormatter {
  private allNumbers: number[] = [];

  constructor(data: FinancialData) {
    this.calculateAllNumbers(data);
  }

  private calculateAllNumbers(data: FinancialData) {
    this.allNumbers = [];

    // Add commission card numbers
    if (data.commissionData && data.contractData) {
      data.commissionData.shipments.forEach(shipment => {
        const commission = this.calculateShipmentCommission(
          shipment.shipmentNumber, 
          data.commissionData!, 
          data.contractData
        );
        this.allNumbers.push(commission);
        this.allNumbers.push(Math.abs(shipment.expenses));
      });
      
      // Add total commission from commission card
      const totalCommission = this.calculateTotalCommission(data.commissionData, data.contractData);
      this.allNumbers.push(totalCommission);
    }

    // Add payment history numbers
    if (data.paymentData) {
      data.paymentData.payments.forEach(payment => {
        this.allNumbers.push(Math.abs(payment.amount));
      });
      this.allNumbers.push(Math.abs(data.paymentData.totalCommission));
    }
  }

  private calculateShipmentCommission(shipmentNumber: string, commissionData: any, contract: any): number {
    const shipment = contract.shipments?.find((s: any) => s.shipmentNumber === shipmentNumber);
    if (!shipment) return 0;

    let totalCommission = 0;

    shipment.containers?.forEach((container: any) => {
      container.goods?.forEach((goodsItem: any) => {
        const details = goodsItem.details;
        const price = parseFloat(details.price) || 0;
        const units = parseFloat(details.units) || 0;
        const lbsEa = parseFloat(details.lbsEa) || 0;

        let goodsCommission = 0;
        if (lbsEa > 0) {
          goodsCommission = (commissionData.commissionRate / 100) * price * units * lbsEa;
        } else {
          goodsCommission = (commissionData.commissionRate / 100) * price * units;
        }

        totalCommission += goodsCommission;
      });
    });

    return totalCommission * (commissionData.commissionShare / 100);
  }

  private calculateTotalCommission(commissionData: any, contract: any): number {
    return commissionData.shipments.reduce((total: number, shipment: any) => {
      const commission = this.calculateShipmentCommission(shipment.shipmentNumber, commissionData, contract);
      const netCommission = commission - Math.abs(shipment.expenses);
      return total + netCommission;
    }, 0);
  }

  public getLongestNumberLength(): number {
    if (this.allNumbers.length === 0) return 8; // Default padding

    const longestLength = Math.max(
      ...this.allNumbers.map(num => 
        num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).length
      )
    );
    
    return longestLength;
  }

  public formatCurrencyWithPadding(amount: number, isNegative: boolean = false): string {
    const longestLength = this.getLongestNumberLength();
    const formattedNumber = Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    // Add 1 extra space so even the longest number has a space after the dollar sign
    const paddingSpaces = ' '.repeat(longestLength - formattedNumber.length + 1);
    
    if (isNegative) {
      return `-$${paddingSpaces}${formattedNumber}`;
    } else {
      return `$${paddingSpaces}${formattedNumber}`;
    }
  }
}